import {
  Box,
  PlanType,
  RadioButtonGroupMapping,
  RadioButtonType,
  RadioButtons,
  TextInput,
} from '@labrav/react-components'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ContractDurationType,
  DiscountType,
} from '../../../../admin/modules/resaleAuthorizations'
import { getKeyFromValue } from '../../../../common/utils/getKeyFromValue'
import { ResaleAuthorizationWithErrors } from '../../../../modules/resaleAuthorization/types'
import { KeyValue } from '../../../../modules/privateOffer/privateOfferCreation/types'
import { useStyles } from './DiscountDetails.styles'
import { isEmpty } from 'lodash'
import { getValidValueBetweenMinMax } from '@labrav/common'
import { ContractDuration } from '../ContractDuration/ContractDuration'
import { disableResaleFlexiblePayment } from '../../constants'

export type DiscountDetailsProps = {
  resaleAuthorizationWithError: ResaleAuthorizationWithErrors
  handleChange: (data: KeyValue) => void
  planType: PlanType
}

export const DiscountDetails = ({
  resaleAuthorizationWithError,
  handleChange,
  planType,
}: DiscountDetailsProps) => {
  const { classes } = useStyles()
  const { errors, discountType } = resaleAuthorizationWithError
  const isDisableFlexiblePayment = planType === PlanType.SAAS_SUBSCRIPTION
  const discountMapping: RadioButtonGroupMapping[] = [
    {
      key: DiscountType.DISCOUNT,
      label: 'Discount',
      type: RadioButtonType.STANDARD,
    },
    {
      key: DiscountType.INDIVIDUAL_PRICING,
      label: 'Individual pricing',
      type: RadioButtonType.STANDARD,
    },
    {
      key: DiscountType.FLEXIBLE_PAYMENT_TERMS,
      label: `Flexible payment schedule ${
        isDisableFlexiblePayment ? disableResaleFlexiblePayment : ''
      }`,
      type: RadioButtonType.STANDARD,
      disabled: isDisableFlexiblePayment,
    },
  ]
  const [percentageDiscountValue, setPercentageDiscountValue] = useState<
    number | undefined
  >(resaleAuthorizationWithError.percentageDiscount)

  const contractDurationReference = useRef(true)
  useEffect(() => {
    if (planType === PlanType.SAAS_CONTRACT) {
      if (!contractDurationReference.current) {
        handleChange({
          key: 'contractDurationType',
          value:
            discountType === DiscountType.DISCOUNT
              ? ''
              : ContractDurationType.STANDARD,
        })
      } else {
        contractDurationReference.current = false
      }
    } else {
      handleChange({
        key: 'contractDurationType',
        value: '',
      })
    }
  }, [discountType, planType])

  useEffect(() => {
    setPercentageDiscountValue(resaleAuthorizationWithError.percentageDiscount)
  }, [resaleAuthorizationWithError.percentageDiscount])

  const handleUpdateDiscountType = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleChange({
        key: 'discountType',
        value: (event.target as HTMLInputElement).value,
      })
    },
    [discountType]
  )

  const handleUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedValue = getValidValueBetweenMinMax(
      event.target.value as string,
      1,
      100
    )
    setPercentageDiscountValue(updatedValue)
    handleChange({
      key: 'percentageDiscount',
      value: updatedValue || '',
    })
  }

  const shouldShowContractDuration =
    discountType &&
    discountType !== DiscountType.DISCOUNT &&
    planType !== PlanType.SAAS_SUBSCRIPTION

  return (
    <Box
      tooltipLabel=""
      heading="Discount details"
      subHeading={'Discount on the product price that the reseller can offer'}
    >
      <div className={classes.container} data-testid="discount-container">
        <RadioButtons
          checkedValue={{
            key: getKeyFromValue(discountType)(DiscountType) as string,
            value: discountType || '',
          }}
          handleChange={handleUpdateDiscountType}
          radioButtonMappings={discountMapping}
        />
        {discountType === DiscountType.DISCOUNT && (
          <div className={classes.percentageDiscount}>
            <TextInput
              isRequired={true}
              keyName="percentageDiscount"
              title="Percentage Discount (%)"
              type="number"
              placeHolder=""
              key="percentageDiscount"
              isReadOnly={false}
              value={resaleAuthorizationWithError.percentageDiscount || ''}
              updatedValue={percentageDiscountValue || ''}
              onChange={handleUpdate}
              isError={!isEmpty(errors?.percentageDiscount as string)}
              errorText={(errors?.percentageDiscount as string) || ''}
              minNumber={1}
              maxNumber={100}
            />
          </div>
        )}
        {shouldShowContractDuration && (
          <ContractDuration
            resaleAuthorizationWithError={resaleAuthorizationWithError}
            handleChange={handleChange}
          />
        )}
      </div>
    </Box>
  )
}
